import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Result, Spin, StepProps, Steps, StepsProps } from "antd";
import useIsMobile from "../../utils/useIsMobile";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../configureStore";
import StepComponent from "../Step";
import { ConfigStepType } from "../../utils/configuration";
import { useSearchParams } from "react-router";
import { Applicant, createApplicant, GetApplicant, getApplicant, SET_CURRENT_STEP, Tokens } from "../../redux/reducer/applicant";
import TinGate from "../organisms/TinGate";
import { AxiosResponse } from "axios";
import { isExpired, tokenApplicantId, tokenSub } from "../../utils";
import { get } from "../../redux/http/request";

interface OrchestratorParams {
}

const Orchestrator = ({
}: OrchestratorParams) => {
    const dispatch = useDispatch<AppDispatch>();
    const isMobile = useIsMobile();
    const [params] = useSearchParams();
    const [authAgain, setAuthAgain] = useState<number>(0);
    const [tinVerified, setTinVerified] = useState<boolean>(false);
    const institutionId = useSelector<RootState, string | undefined>(({ configuration }) => configuration.institution?.institutionId);
    const requireLast4Tin = useSelector<RootState, boolean>(({ configuration }) => configuration.institution?.requireLast4Tin ?? false);
    const applicant = useSelector<RootState, Applicant | null>(({ applicant: a }) => a.applicant);
    const currentStep = useSelector<RootState, number>(({ applicant: a }) => a.currentStep);
    const services = useSelector<RootState, ConfigStepType[]>(({ configuration }) => configuration.steps ?? []);
    const errorSteps = useSelector<RootState, { [id: string]: boolean }>(({ applicant: a }) => a.errorSteps ?? {});
    const activeServices = useMemo<ConfigStepType[]>(() => {
        if (!applicant?.steps) return services;
        const applicantStepIds = new Set(applicant.steps.map(s => s.id));
        return services.filter(s => applicantStepIds.has(s.id));
    }, [services, applicant?.steps]);
    const applicantId = useMemo(() => (
        applicant?.applicantId ?? params.get('id') ?? tokenApplicantId(localStorage.getItem('accessToken'))
    ), [applicant, params]);
    const hasApplicant = useSelector<RootState, boolean>(({ applicant: { applicant: a } }) => Boolean(a));

    // Track previous step to detect transitions for postMessage
    const prevStepRef = useRef<number | null>(null);

    useEffect(() => {
        // Skip if not in iframe
        if (window.parent === window) return;

        // Skip initial render
        if (prevStepRef.current === null) {
            prevStepRef.current = currentStep;
            return;
        }

        // Only post if step actually increased (step completed)
        if (currentStep > prevStepRef.current && activeServices.length > 0) {
            const completedStepIndex = prevStepRef.current;
            const completedStepConfig = activeServices[completedStepIndex];

            try {
                window.parent.postMessage({
                    type: 'step-completed',
                    source: 'halcyondirect',
                    completedStep: completedStepConfig ? {
                        index: completedStepIndex,
                        id: completedStepConfig.id,
                        name: completedStepConfig.nameOverride ?? completedStepConfig.name,
                    } : null,
                    nextStep: activeServices[currentStep] ? {
                        index: currentStep,
                        id: activeServices[currentStep].id,
                        name: activeServices[currentStep].nameOverride ?? activeServices[currentStep].name,
                    } : null,
                    allComplete: currentStep >= activeServices.length,
                }, '*');
            } catch (error) {
                console.error('Error sending step-completed message to parent:', error);
            }
        }

        prevStepRef.current = currentStep;
    }, [currentStep, activeServices]);

    const setCurrentStep = useCallback((newStep: number) => {
        dispatch({ type: SET_CURRENT_STEP, newStep })
    }, [dispatch]);

    const hasCalled = useRef(false);

    const makeApplicant = useCallback(() => {
        const createApplicantId = params.get('id');
        const cId = params.get('cid') ?? undefined;
        const bundleId = params.get('bundleId') ?? undefined;
        const encompassBorrowerId = params.get('borrowerId') ?? undefined;
        const pii = {
            name: params.get('name') ?? undefined,
            firstName: params.get('firstName') ?? params.get('firstname') ?? undefined,
            lastName: params.get('lastName') ?? params.get('lastname') ?? undefined,
            tin: params.get('tin') ?? undefined,
            email: params.get('email') ?? undefined,
            addressLine1: params.get('addressLine1') ?? params.get('addressline1') ?? undefined,
            addressLine2: params.get('addressLine2') ?? params.get('addressline2') ?? undefined,
            city: params.get('city') ?? undefined,
            state: params.get('state') ?? undefined,
            postalCode: params.get('postalCode') ?? params.get('postalcode') ?? undefined,
        }

        // Parse businesses from URL params
        let businesses: any[] | undefined;
        const businessesParam = params.get('businesses');
        if (businessesParam) {
            try {
                businesses = JSON.parse(decodeURIComponent(businessesParam));
            } catch {
                console.warn('Failed to parse businesses from URL params');
            }
        }

        dispatch(createApplicant({
            input: {
                institutionId: institutionId ?? '',
                applicantId: createApplicantId ?? '',
                cId,
                pii,
                encompassBorrowerId,
                bundleId,
                businesses,
            },
            onSuccess: (res: AxiosResponse<{ applicant: Applicant; tokens?: Tokens; }>) => {
                if (res.data.tokens) {
                    localStorage.setItem('accessToken', res.data.tokens.accessToken);
                    const accessTokens = JSON.stringify([...JSON.parse(localStorage.getItem('accessTokens') ?? '[]') as string[], res.data.tokens.accessToken]);
                    localStorage.setItem('accessTokens', accessTokens);
                    if (res.data.tokens.refreshToken) {
                        localStorage.setItem('refreshToken', res.data.tokens.refreshToken);
                        const refreshTokens = JSON.stringify([...JSON.parse(localStorage.getItem('refreshTokens') ?? '[]') as string[], res.data.tokens.refreshToken]);
                        localStorage.setItem('refreshTokens', refreshTokens);
                    }
                }
            }
        }));
    }, [params, applicant]);

    useEffect(() => {
        if (hasCalled.current) return;
        hasCalled.current = true;

        if (institutionId) {
            const input: GetApplicant = {
                applicantId,
                institutionId,
            };
            const tempToken = params.get('token');
            if (tempToken) input.token = tempToken;
            const headers: { Authorization?: string; refresh?: string } = {};
            let shouldCall = true;
            let accessToken = localStorage.getItem('accessToken');
            if (accessToken && !isExpired(accessToken)) headers.Authorization = `Bearer ${accessToken ?? ''}`;
            else if (localStorage.getItem('accessToken') && localStorage.getItem('refreshToken') && !isExpired(localStorage.getItem('refreshToken'))) {
                shouldCall = false;
                get('/auth', {}, undefined, { refresh: localStorage.getItem('refreshToken') })
                    .then((res) => {
                        localStorage.setItem('accessToken', res.data.accessToken);
                        const accessTokens = JSON.stringify([...JSON.parse(localStorage.getItem('accessTokens') ?? '[]') as string[], res.data.accessToken]);
                        localStorage.setItem('accessTokens', accessTokens);
                        setAuthAgain(authAgain + 1);
                    })
                    .catch();
            }

            // ?id= may carry either the applicant's id (sub/uid claim) or its applicantId (aid claim)
            const tokenMatchesId = (t: string | null, id: string) => (tokenSub(t) === id || tokenApplicantId(t) === id);

            const paramId = params.get('id');
            if (paramId && !tokenMatchesId(accessToken, paramId)) {
                const accessTokens: string[] = JSON.parse(localStorage.getItem('accessTokens') ?? '[]');
                const matched = accessTokens.find((t) => (tokenMatchesId(t, paramId) && !isExpired(t))) ?? null;
                if (matched) {
                    accessToken = matched;
                    localStorage.setItem('accessToken', accessToken);
                    const sub = tokenSub(accessToken);
                    const refreshTokens: string[] = JSON.parse(localStorage.getItem('refreshTokens') ?? '[]');
                    const refreshToken = refreshTokens.find((t) => (tokenSub(t) === sub && !isExpired(t))) ?? null;
                    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
                    headers.Authorization = `Bearer ${accessToken}`;
                }
                // no matching token: leave whatever header we already had rather than sending "Bearer null"
            }

            if (shouldCall) {
                dispatch(getApplicant({
                    input,
                    headers,
                    onSuccess: (res: AxiosResponse<{ applicant: Applicant | null; createOnNull: boolean; tokens?: Tokens }>) => {
                        if (res.status === 200 && res.data.applicant === null && res.data.createOnNull && (institutionId && applicantId)) {
                            makeApplicant();
                        } else if (res.status === 200 && res.data.tokens) {
                            localStorage.setItem('accessToken', res.data.tokens.accessToken);
                            const accessTokens = JSON.stringify([...JSON.parse(localStorage.getItem('accessTokens') ?? '[]') as string[], res.data.tokens.accessToken]);
                            localStorage.setItem('accessTokens', accessTokens);
                            if (res.data.tokens.refreshToken) {
                                localStorage.setItem('refreshToken', res.data.tokens.refreshToken);
                                const refreshTokens = JSON.stringify([...JSON.parse(localStorage.getItem('refreshTokens') ?? '[]') as string[], res.data.tokens.refreshToken]);
                                localStorage.setItem('refreshTokens', refreshTokens);
                            }
                        }
                    }
                }));
            }
        }
    }, [authAgain]);

    const items = useMemo<StepsProps['items']>(() => activeServices.map<StepProps>((step) => ({
        title: step.nameOverride ?? step.name,
        status: errorSteps[step.id] ? 'error' : undefined,
    })), [activeServices, errorSteps]);

    if (hasApplicant && requireLast4Tin && !tinVerified && institutionId && applicantId) {
        return <TinGate institutionId={institutionId} applicantId={applicantId} onVerified={() => setTinVerified(true)} />;
    }

    return hasApplicant ? (
        <div className={isMobile ? "py-8" : "pa-8"}>
            {
                currentStep >= activeServices.length ? (
                    <Result
                        status="success"
                        title="You are all set!"
                    />
                ) : (
                    <>
                        <Steps
                            className='pa-4 mb-4'
                            items={items}
                            current={currentStep}
                        />
                        {activeServices[currentStep] && (
                            <StepComponent
                                step={activeServices[currentStep]}
                                onContinue={() => { setCurrentStep(currentStep + 1); }}
                            />)}
                    </>
                )
            }
        </div>
    ) : <Spin fullscreen />;
};

export default Orchestrator;
